(function modalAccessibilityFactory(root) {
  'use strict';

  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  const modalElements = new Map();
  const inertState = new WeakMap();

  function isFocusable(element) {
    if (!element || element.closest?.('[inert], [aria-hidden="true"]')) {
      return false;
    }

    if (typeof element.getClientRects === 'function' && element.getClientRects().length === 0) {
      return false;
    }

    const style = typeof root.getComputedStyle === 'function'
      ? root.getComputedStyle(element)
      : null;

    return !style || (style.display !== 'none' && style.visibility !== 'hidden');
  }

  function getFocusableElements(container) {
    if (!container || typeof container.querySelectorAll !== 'function') {
      return [];
    }

    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter(isFocusable);
  }

  function retainInert(element) {
    const state = inertState.get(element) || {
      count: 0,
      wasInert: Boolean(element.inert),
      hadInertAttribute: element.hasAttribute('inert'),
    };

    state.count += 1;
    inertState.set(element, state);
    element.inert = true;
    element.setAttribute('inert', '');
  }

  function releaseInert(element) {
    const state = inertState.get(element);
    if (!state) {
      return;
    }

    state.count -= 1;
    if (state.count > 0) {
      return;
    }

    element.inert = state.wasInert;
    if (state.hadInertAttribute) {
      element.setAttribute('inert', '');
    } else {
      element.removeAttribute('inert');
    }
    inertState.delete(element);
  }

  function getManagedInertAncestors(modalRoot) {
    const elements = [];
    let element = modalRoot;

    while (element) {
      if (inertState.has(element)) {
        elements.push(element);
      }
      element = element.parentElement;
    }

    return elements;
  }

  function suspendManagedInert(element) {
    const state = inertState.get(element);
    if (!state || state.count < 1) {
      return;
    }

    element.inert = false;
    element.removeAttribute('inert');
  }

  function restoreManagedInert(element) {
    const state = inertState.get(element);
    if (!state || state.count < 1) {
      return;
    }

    element.inert = true;
    element.setAttribute('inert', '');
  }

  function getBackgroundBranches(modalRoot) {
    const elements = [];
    let activeBranch = modalRoot;
    let parent = activeBranch?.parentElement;

    while (parent) {
      Array.from(parent.children).forEach((child) => {
        if (child !== activeBranch) {
          elements.push(child);
        }
      });

      if (parent === root.document?.body) {
        break;
      }

      activeBranch = parent;
      parent = activeBranch.parentElement;
    }

    return Array.from(new Set(elements));
  }

  function activate(modalRoot) {
    if (!modalRoot || modalElements.has(modalRoot)) {
      return;
    }

    const suspendedAncestors = getManagedInertAncestors(modalRoot);
    suspendedAncestors.forEach(suspendManagedInert);
    const backgroundElements = getBackgroundBranches(modalRoot);
    backgroundElements.forEach(retainInert);
    modalElements.set(modalRoot, {
      backgroundElements,
      suspendedAncestors,
    });
  }

  function deactivate(modalRoot) {
    const state = modalElements.get(modalRoot);
    if (!state) {
      return;
    }

    state.backgroundElements.forEach(releaseInert);
    state.suspendedAncestors.forEach(restoreManagedInert);
    modalElements.delete(modalRoot);
  }

  function trapFocus(event, container) {
    if (!event || event.key !== 'Tab') {
      return;
    }

    const focusable = getFocusableElements(container);
    if (!focusable.length) {
      event.preventDefault();
      container?.focus?.({ preventScroll: true });
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const activeElement = root.document?.activeElement;

    if (event.shiftKey) {
      if (activeElement === first || !container?.contains(activeElement)) {
        event.preventDefault();
        last.focus();
      }
      return;
    }

    if (activeElement === last || !container?.contains(activeElement)) {
      event.preventDefault();
      first.focus();
    }
  }

  root.themeModalAccessibility = {
    activate,
    deactivate,
    getFocusableElements,
    trapFocus,
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      FOCUSABLE_SELECTOR,
      activate,
      deactivate,
      getBackgroundBranches,
      getFocusableElements,
      trapFocus,
    };
  }
})(typeof window !== 'undefined' ? window : globalThis);
